import React, { useState } from 'react';
import { ArrowLeft, Phone, Globe, Instagram, Facebook, Twitter, Home, Compass, Ticket, LifeBuoy, SlidersHorizontal, Signal, Wifi, BatteryFull, Tag, CalendarOff, CalendarClock } from 'lucide-react';
import { S3Image } from './S3Image';
import { useProducts } from '../hooks/useProducts';
import { findProductIcon } from '../utils/productIcons';

interface BusinessAppPreviewProps {
  name: string;
  logoKey: string;
  backgroundKey: string;
  description?: string;
  phone?: string;
  websiteUrl?: string;
  instagramUrl?: string;
  xUrl?: string;
  facebookUrl?: string;
  /** "Open" / "Closed" — derived from the business schedule. */
  openLabel?: string | null;
  isOpen?: boolean;
  businessId?: string;
  /**
   * SAM-650 — the next business closure or special day, as the app announces it on the profile
   * (e.g. "Closed Dec 24 – Dec 26" · "Christmas"). Omitted when there is none ahead.
   */
  calendarNotice?: { title: string; detail?: string; tone: 'closed' | 'special' } | null;
}

const monogram = (name: string): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'B';
  return ((parts[0][0] || '') + (parts[1]?.[0] || '')).toUpperCase();
};

const cleanUrl = (url: string): string => url.replace(/^https?:\/\//, '').replace(/\/$/, '');

const NAV: Array<{ Icon: React.ComponentType<any>; label: string; active?: boolean }> = [
  { Icon: Home, label: 'Home' },
  { Icon: Compass, label: 'Explore', active: true },
  { Icon: Ticket, label: 'Activity' },
  { Icon: LifeBuoy, label: 'Help' },
  { Icon: SlidersHorizontal, label: 'Manage' },
];

/**
 * Phone mockup that mirrors the real mobile app business profile. Tabs are
 * interactive: Services shows the business's real service cards; About shows
 * the description and the contact links the user configured. Live from the form.
 */
export const BusinessAppPreview: React.FC<BusinessAppPreviewProps> = ({
  name,
  logoKey,
  backgroundKey,
  description,
  phone,
  websiteUrl,
  instagramUrl,
  xUrl,
  facebookUrl,
  openLabel,
  isOpen,
  businessId,
  calendarNotice,
}) => {
  const { data } = useProducts(businessId || null);
  const products = (data ?? []).slice(0, 6);
  const hasServices = products.length > 0;
  const [tab, setTab] = useState<'services' | 'about'>('services');
  const [expanded, setExpanded] = useState(false);

  return (
    <aside className="preview-rail">
      <div className="pv-stage-label">App preview</div>
      <div className="pv-phone">
        <div className="pv-screen">
          <div className="pv-statusbar">
            <span className="pv-time">9:41</span>
            <span className="pv-status-icons">
              <Signal size={13} />
              <Wifi size={13} />
              <BatteryFull size={16} />
            </span>
          </div>

          <div className="pv-appbar">
            <ArrowLeft size={18} />
            <span className="pv-appbar-title">{name || 'Business'}</span>
            <span className="pv-appbar-spacer" />
          </div>

          <div className="pv-scroll">
            <div className="pv-cover">
              {backgroundKey ? (
                <S3Image imageKey={backgroundKey} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div className="pv-cover-strap" />
              )}
            </div>

            <div className="pv-profile">
              <div className="pv-profile-logo">
                {logoKey ? (
                  <S3Image imageKey={logoKey} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span>{monogram(name)}</span>
                )}
              </div>
              <div className="pv-profile-name">{name || 'Business name'}</div>
              <div className="pv-profile-right">
                {openLabel && (
                  <span className={`pv-open ${isOpen ? 'is-open' : 'is-closed'}`}>{openLabel}</span>
                )}
                <span className="pv-phone-ic"><Phone size={15} fill="currentColor" stroke="currentColor" /></span>
              </div>
            </div>

            {calendarNotice && (
              <div
                role="note"
                aria-label="Upcoming closure or special day"
                data-testid="pv-calendar-notice"
                className={`mx-3 mt-2 mb-1 flex items-start gap-2 rounded-[10px] px-3 py-2 text-[12px] leading-snug ${calendarNotice.tone === 'closed'
                  ? 'bg-[var(--bad-bg,#fdecec)] text-[var(--bad-fg,#a32020)]'
                  : 'bg-[var(--warn-bg-2,#fff5e0)] text-[var(--warn-fg,#8a5a00)]'}`}
              >
                {calendarNotice.tone === 'closed' ? <CalendarOff size={14} className="shrink-0 mt-px" /> : <CalendarClock size={14} className="shrink-0 mt-px" />}
                <span className="min-w-0">
                  <span className="block font-semibold">{calendarNotice.title}</span>
                  {calendarNotice.detail && <span className="block opacity-90">{calendarNotice.detail}</span>}
                </span>
              </div>
            )}

            {hasServices && (
              <div className="pv-tabs">
                <span className={tab === 'services' ? 'is-active' : ''} onClick={() => setTab('services')}>Services</span>
                <span className={tab === 'about' ? 'is-active' : ''} onClick={() => setTab('about')}>About</span>
              </div>
            )}

            {hasServices && tab === 'services' ? (
              <div className="pv-cards">
                {products.length > 0 ? (
                  products.map((p, i) => {
                    const Icon = findProductIcon(p.icon_name)?.lucide || Tag;
                    return (
                      <div className="pv-card" key={(p as any).id ?? i}>
                        <Icon size={22} />
                        <span>{p.name}</span>
                      </div>
                    );
                  })
                ) : (
                  <div className="pv-cards-empty">Services will appear here.</div>
                )}
              </div>
            ) : (
              <div className="pv-about">
                <div className="pv-about-title">About</div>
                {description ? (
                  <>
                    <div className={`pv-about-desc ${expanded ? '' : 'clamp'}`}>{description}</div>
                    {!expanded && description.length > 110 && (
                      <div className="pv-readmore" onClick={() => setExpanded(true)}>Read more</div>
                    )}
                  </>
                ) : (
                  <div className="pv-about-desc" style={{ color: '#9CA3AF' }}>No description yet.</div>
                )}

                <div className="pv-contact-list">
                  {phone && phone.trim() && (
                    <div className="pv-contact-row"><Phone size={16} fill="currentColor" stroke="currentColor" /><span>{phone}</span></div>
                  )}
                  {websiteUrl && websiteUrl.trim() && (
                    <div className="pv-contact-row"><Globe size={16} /><span>{cleanUrl(websiteUrl)}</span></div>
                  )}
                  {instagramUrl && instagramUrl.trim() && (
                    <div className="pv-contact-row"><Instagram size={16} /><span>Instagram</span></div>
                  )}
                  {xUrl && xUrl.trim() && (
                    <div className="pv-contact-row"><Twitter size={16} /><span>X</span></div>
                  )}
                  {facebookUrl && facebookUrl.trim() && (
                    <div className="pv-contact-row"><Facebook size={16} /><span>Facebook</span></div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="pv-bottom">
            <button type="button" className="pv-schedule" disabled>Schedule</button>
            <div className="pv-nav">
              {NAV.map(({ Icon, label, active }) => (
                <span key={label} className={`pv-nav-item ${active ? 'is-active' : ''}`}>
                  <Icon size={16} />
                  <span>{label}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};
